import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import ItemMenuOptions from './ItemMenuOptions'
import { DesktopLayoutSizes } from './utils'
import type { ItemCardVariant } from '../types'

import BookedLabel from 'tripPlanner/components/BookedLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import { AddRemoveItineraryButton } from 'tripPlanner/components/Itinerary/AddRemoveItineraryButton'
import ItineraryDateButton from 'tripPlanner/components/Itinerary/ItineraryDateButton'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import AddedUpdatedBy from 'tripPlanner/components/TripModal/TripItems/Summary/Common/AddedUpdatedBy'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesTP'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ItemMetadata } from 'tripPlanner/models'
import ItemTileMenuButton from 'tripPlanner/Tripkit/Core/MenuButton/MenuButton'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { isCustomItem } from 'tripPlanner/utils'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

const ActionsGroup = styled(Group)`
  margin-inline-start: auto;
`

const MenuButton = styled(ItemTileMenuButton)`
  margin-right: ${rem(-4)};
`

const StyledTopContent = styled.div`
  width: 100%;
`

const PaddedBox = styled.div`
  padding: ${rem(12)};
`

const DesktopLayoutOnlyContainer = styled(Group)`
  display: none;

  ${DesktopLayoutSizes} {
    display: flex;
  }
`

interface Props {
  item: TripItem
  children: React.ReactNode
  variant: ItemCardVariant
  nonInteractable?: boolean
}

function TopContent({ item, children, nonInteractable, variant }: Props) {
  const { type, createdAt, createdBy, userUpdatedAt, updatedBy, tripId } = item
  const { icon: Icon } = ItemMetadata[type]

  const openModal = useOpenModal()

  const canEdit = useCanEditTrip()
  const isMenuVisibleAlways =
    item.type === 'NOTE' ||
    item.type === 'BUS' ||
    item.type === 'TRAIN' ||
    item.type === 'FERRY' ||
    item.type === 'OTHER_TRANSPORT'
  const menuButton = useMemo(
    () => (
      <MenuButton
        title=""
        bodyContent={<ItemMenuOptions item={item} />}
        footerContent={
          createdAt &&
          createdBy &&
          userUpdatedAt &&
          updatedBy && (
            <PaddedBox>
              {/* THIS IS A HACK
        these fields will not exist on public trip items
        this is a temporary hack to ensure public trip can utilise the
        viewer trip context values for stable application behaviour
      */}
              <AddedUpdatedBy
                tripId={tripId}
                createdAt={createdAt}
                createdBy={createdBy}
                updatedAt={userUpdatedAt}
                updatedBy={updatedBy}
                avatarSize="small"
              />
            </PaddedBox>
          )
        }
      />
    ),
    [createdAt, createdBy, item, tripId, updatedBy, userUpdatedAt],
  )

  const addRemoveItineraryButton = useMemo(() => {
    const onRequestAddTripDates = (
      onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
    ) => {
      openModal(EditTripDates, { tripId, onDatesAdded })
    }

    if (isCustomItem(item)) {
      return (
        <ItineraryDateButton
          item={item}
          variant="chip"
          onRequestAddTripDates={onRequestAddTripDates}
        />
      )
    } else {
      return (
        <AddRemoveItineraryButton
          item={item}
          onClickAddTripItemDates={() => openModal(EditTripItem, { item })}
        />
      )
    }
  }, [item, openModal, tripId])

  return (
    <StyledTopContent>
      <Group
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="center"
        wrap="wrap-reverse"
        gap={2}
      >
        <BodyTextBlock
          variant="small"
          startIcon={<Icon size="XS" />}
          wrap="truncate"
        >
          {children}
        </BodyTextBlock>
        <ActionsGroup
          direction="horizontal"
          horizontalAlign="end"
          verticalAlign="center"
          gap={8}
        >
          {'isBooked' in item && item.isBooked && <BookedLabel />}
          {!nonInteractable && canEdit && !isMenuVisibleAlways && (
            <DesktopLayoutOnlyContainer
              direction="horizontal"
              gap={8}
              verticalAlign="center"
            >
              {(variant === 'tripSummary' || variant === 'mapSummary') &&
                addRemoveItineraryButton}
              {menuButton}
            </DesktopLayoutOnlyContainer>
          )}
          {!nonInteractable && canEdit && isMenuVisibleAlways && (
            <Group direction="horizontal" gap={8} verticalAlign="center">
              {(variant === 'tripSummary' || variant == 'mapSummary') &&
                addRemoveItineraryButton}
              {menuButton}
            </Group>
          )}
        </ActionsGroup>
      </Group>
    </StyledTopContent>
  )
}

export default TopContent
